import {SetStateAction, useEffect, useRef, useState} from 'react';
import {useGetDocMetadataFilterOptionsQuery} from '../../../../hooks/document/useDocument';
import {ISelectItem} from '../../../../shared/types';
import ArrMultiSelectLazyLoadDropdown from '../../../common/arr-select-dropdown/ArrMultiSelectLazyLoadDropdown';
import {ActiveFilter} from '../filter-accordion/ArrFilterAccordion';
import {t} from 'i18next';

export interface LazyLoadFilter {
  setFilterData: (value: SetStateAction<ActiveFilter>) => void;
  filterData: ActiveFilter;
  id: string;
  useDataTestIdsSimilarToArrMultiSelect?: boolean;
  checkboxDataTestId?: string;
}
const LazyLoadFilterList: React.FC<LazyLoadFilter> = ({
  setFilterData,
  filterData,
  id,
  useDataTestIdsSimilarToArrMultiSelect,
  checkboxDataTestId = 'arr-filter-panel-lazy-load-filter-list-dropdown-checkbox',
}) => {
  const {mutateAsync: fetchFilterOptions, isPending: isLoadingMore} =
    useGetDocMetadataFilterOptionsQuery();

  const [page, setPage] = useState(1);
  const [options, setOptions] = useState<ISelectItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const [search, setSearch] = useState('');
  useEffect(() => {
    setPage(1);
    setOptions([]);
    setHasMore(true);
    setSearch('');
  }, [id]);
  const isFetchingRef = useRef(false);
  const fetchPage = async (pageNum: number, searchStr = '') => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    const payload = {
      FieldId: id,
      Query: encodeURIComponent(searchStr),
      PageNumber: pageNum,
      PageSize: 50,
    };
    const res = await fetchFilterOptions(payload);
    isFetchingRef.current = false;
    const newData = res?.data || [];
    const nonEmptyData = newData.filter(
      (item) => item.text && item.text.trim() !== ''
    );
    setOptions((prev) =>
      pageNum === 1 ? nonEmptyData : [...prev, ...nonEmptyData]
    );
    setHasMore((nonEmptyData?.length ?? 0) >= 50);
  };

  // called when dropdown opens or when search changes (debounced)
  const handleDropdownOpen = (searchStr?: string) => {
    setSearch(searchStr ?? '');
    setPage(1);
    fetchPage(1, searchStr ?? '');
  };

  // called when scroll triggers load more
  const handleLoadMore = (searchStr?: string) => {
    if (!hasMore || isLoadingMore) return;
    const next = page + 1;
    setPage(next);
    fetchPage(next, searchStr ?? search);
  };
  return (
    <ArrMultiSelectLazyLoadDropdown
      key={id}
      data-testid="multi-select-filter-dropdown"
      checkboxDataTestId={checkboxDataTestId}
      aria-label={filterData.label}
      dropdownLabel={filterData.label}
      dropdownItems={options}
      dropdownItemKey="text"
      dropdownItemDisplay="text"
      selectedValues={filterData.values ?? []}
      onSelectionChange={(val) =>
        setFilterData((prev) => ({...prev, values: val}))
      }
      multiple
      searchable
      shouldLoadOnOpen
      onDropdownOpen={handleDropdownOpen}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      noResultText={t('arrFilterAccordion.noResultText')}
      useDataTestIdsSimilarToArrMultiSelect={
        useDataTestIdsSimilarToArrMultiSelect
      }
    />
  );
};

export default LazyLoadFilterList;
